import type { ApiClient } from "@riea-water/api-client";
import type { FieldJob } from "@riea-water/contracts";
import { useState, type FormEvent } from "react";
import { useParties, useRaiseFieldJob } from "../api/queries.js";
import { dueText, JOB_TYPE_LABEL, JOB_TYPES } from "./field-helpers.js";

/**
 * Raise field job (FLD-01, D-59, D-60), from a finding or a supply point
 * record. From a finding the type defaults to the rule's; the assignee is the
 * in-house field team or a contractor.
 */
export function RaiseJobSheet(props: {
  client: ApiClient;
  findingId?: string | undefined;
  supplyPointId?: string | undefined;
  onClose: () => void;
  onDone: (job: FieldJob) => void;
}) {
  const raise = useRaiseFieldJob(props.client);
  const parties = useParties(props.client);
  const [error, setError] = useState<string | null>(null);
  const contractors = (parties.data ?? []).filter((p) => p.role === "contractor");

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    const data = new FormData(event.currentTarget);
    const type = data.get("type");
    const assignee = data.get("assignee");
    try {
      const job = await raise.mutateAsync({
        findingId: props.findingId,
        supplyPointId: props.findingId ? undefined : props.supplyPointId,
        type: typeof type === "string" && type !== "" ? (type as FieldJob["type"]) : undefined,
        assigneePartyId: typeof assignee === "string" && assignee !== "" ? assignee : undefined,
      });
      props.onDone(job);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <form method="dialog" onSubmit={(e) => void onSubmit(e)}>
      <h2 id="raise-job-title" style={{ margin: 0 }}>
        Raise field job
      </h2>
      <label>
        Type
        <select name="type" defaultValue="" required={!props.findingId}>
          {props.findingId ? (
            <option value="">The rule's job</option>
          ) : (
            <option value="" disabled>
              Choose
            </option>
          )}
          {JOB_TYPES.map((t) => (
            <option key={t} value={t}>
              {JOB_TYPE_LABEL[t]}
            </option>
          ))}
        </select>
      </label>
      <label>
        Assigned to
        <select name="assignee" defaultValue="">
          <option value="">In-house field team</option>
          {contractors.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </label>
      <div className="faint" style={{ fontSize: ".76rem" }}>
        The job is due by its type's days and carries a brief: the finding, where the meter is, how
        to reach it and what to capture.
      </div>
      {error ? (
        <div className="pill crit" role="alert" style={{ whiteSpace: "normal" }}>
          {error}
        </div>
      ) : null}
      <div className="split" style={{ justifyContent: "flex-end" }}>
        <button className="btn sm" type="button" onClick={props.onClose}>
          Cancel
        </button>
        <button className="btn sm primary" type="submit" disabled={raise.isPending}>
          Raise the job
        </button>
      </div>
    </form>
  );
}

export function raisedMessage(job: FieldJob): string {
  return `${job.ref} raised · ${JOB_TYPE_LABEL[job.type]} · due ${dueText(job)} · FieldJobRaised recorded`;
}
